import React, { useState } from "react";
import Label from "./custom-inputs/Label";
import CustomInput from "./custom-inputs/CustomInput";
import ErrorFormik from "./ErrorFormik";
import DeletionCircle from "./svg/DeletionCircleSvg";
import EditSvg from "./svg/EditSvg";
import PlusIcon from "./svg/PlusIconSvg";
import AcceptSvg from "./svg/AcceptSvg";
import GeneralSelect from "./custom-inputs/select-inputs/GeneralSelect";
import { toast } from "react-toastify";
import * as Yup from "yup";

// Discount is a flat per-person amount subtracted from the experience base
// price, so it must not exceed that base price.
const makeValidationSchema = (basePrice = 0) =>
  Yup.object().shape({
    personType: Yup.string().required("Person Type is required"),
    minUnit: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(1, "Min Unit must be greater than or equal to 1")
      .required("Min Unit is required"),
    maxUnit: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .min(1, "Max Unit must be greater than 1")
      .test(
        "is-minUnit-greater-maxUnit",
        "Max Unit must be greater than Min Unit",
        function (value) {
          const { minUnit } = this.parent;
          if (!value) return true;
          return minUnit <= value;
        },
      )
      .required("Max Unit is required"),
    discountAmount: Yup.number()
      .transform((value) => (value ? value : undefined))
      .test(
        "is-valid-discount",
        "Discount must be less than or equal to the base price",
        function (value) {
          if (!value) return true;
          return Number(basePrice) >= value;
        },
      ),
  });

function validateNoOverlap(newEntry, existingEntries) {
  const newMin = Number(newEntry.minUnit);
  const newMax = Number(newEntry.maxUnit);

  for (const entry of existingEntries) {
    const min = Number(entry.minUnit);
    const max = Number(entry.maxUnit);

    if (entry.personType === newEntry.personType) {
      if (newMin === newMax && newMin === min && newMax === max) {
        return true; // Overlap detected
      } else if (
        (newMin <= max && newMax >= min) ||
        (min <= newMax && max >= newMin)
      ) {
        return true; // Overlap detected
      }
    }
  }

  return false; // No overlap
}

function validateContinuousRanges(newEntry, existingEntries) {
  const newMin = Number(newEntry.minUnit);
  const newMax = Number(newEntry.maxUnit);
  
  // Get all entries for the same person type, including the new entry
  const sameTypeEntries = existingEntries
    .filter(entry => entry.personType === newEntry.personType)
    .concat([newEntry])
    .map(entry => ({
      ...entry,
      minUnit: Number(entry.minUnit),
      maxUnit: Number(entry.maxUnit)
    }))
    .sort((a, b) => a.minUnit - b.minUnit);

  // Check for gaps between ranges
  for (let i = 0; i < sameTypeEntries.length - 1; i++) {
    const current = sameTypeEntries[i];
    const next = sameTypeEntries[i + 1];
    
    // Check if there's a gap between current max and next min
    if (current.maxUnit + 1 !== next.minUnit) {
      return {
        isValid: false,
        message: `Gap detected: Range ${current.minUnit}-${current.maxUnit} should be followed by ${current.maxUnit + 1}, but found ${next.minUnit}`
      };
    }
  }

  // Check if the first range starts from 1
  // if (sameTypeEntries.length > 0 && sameTypeEntries[0].minUnit !== 1) {
  //   return {
  //     isValid: false,
  //     message: `First range for ${newEntry.personType} must start from 1, but starts from ${sameTypeEntries[0].minUnit}`
  //   };
  // }

  return { isValid: true };
}

export default function DynamicPricingFields({
  onChange,
  val,
  errors,
  touched,
  inputFields,
  fieldName,
  textButton,
  NewEntryShap,
  tableLabel,
  basePrice = 0,
}) {

  const [newEntry, setNewEntry] = useState(NewEntryShap);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editIndex, setEditIndex] = useState(undefined);
  const validationSchema = makeValidationSchema(basePrice);

  const handleAddFields = async () => {
    try {
      await validationSchema.validate(newEntry, { abortEarly: false });
      
      // Check for overlaps
      if (validateNoOverlap(newEntry, val)) {
        toast.error("Overlapping units detected for the same person type.");
        return;
      }

      // Check for continuous ranges (gap validation)
      const continuityValidation = validateContinuousRanges(newEntry, val);
      if (!continuityValidation.isValid) {
        toast.error(continuityValidation.message);
        return;
      }

      onChange([...val, newEntry]);
      setNewEntry(NewEntryShap);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorMessage = error.errors[0]; // .join(", ");
        toast.error(errorMessage);
      } else {
        // Handle other types of errors
        console.log(error)
        console.error("Error:", error);
        toast.error("An error occurred. Please try again later.");
      }
    }
  };

  const handleEditFields = async () => {
    try {
      await validationSchema.validate(newEntry, { abortEarly: false });
      
      const valuesWithoutEditIndex = val.filter(
        (_, index) => editIndex !== index,
      );
      
      // Check for overlaps
      if (validateNoOverlap(newEntry, valuesWithoutEditIndex)) {
        toast.error("Overlapping units detected for the same person type.");
        return;
      }

      // Check for continuous ranges (gap validation)
      const continuityValidation = validateContinuousRanges(newEntry, valuesWithoutEditIndex);
      if (!continuityValidation.isValid) {
        toast.error(continuityValidation.message);
        return;
      }

      setIsEditMode(false);
      setEditIndex(undefined);

      onChange([...valuesWithoutEditIndex, newEntry]);
      setNewEntry(NewEntryShap);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const errorMessage = error.errors[0]; // .join(", ");
        toast.error(errorMessage);
      } else {
        // Handle other types of errors
        console.error("Error:", error);
        toast.error("An error occurred. Please try again later.");
      }
    }
  };

  const handleRemoveFields = (index) => {
    const values = [...val];
    values.splice(index, 1);
    onChange(values);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditIndex(undefined);
    setNewEntry(NewEntryShap);
  };

  const handleChangeNewEntry = (name, value) => {
    setNewEntry({ ...newEntry, [name]: value });
  };

  const handelEditField = async (index) => {
    setTimeout(() => {
      window.scrollTo(0, 300);
    }, 2);

    setEditIndex(index);
    setNewEntry(val[index]);
    setIsEditMode(true);
    
  };

  val.sort((a, b) => {
    if (a.personType < b.personType) {
      return -1;
    }
    if (a.personType > b.personType) {
      return 1;
    }
    return a.minUnit - b.minUnit;
  });
  const finalPricePreview = Math.max(
    0,
    Number(basePrice) - (Number(newEntry?.discountAmount) || 0),
  );

  return (
    <div>
      {/* Add / Edit form card */}
      <div className='border rounded p-3 mb-4'>
        <h5 className='mb-3'>
          {isEditMode
            ? `Editing: ${newEntry?.personType || "—"} · ${
                newEntry?.minUnit || "?"
              }–${newEntry?.maxUnit || "?"} guests`
            : textButton || "Add a pricing slot"}
        </h5>
        {/* Input fields */}
        <div className='w-100 row g-3'>
          {inputFields?.map((input, inputIndex) => (
            <div className={`col-lg-6`} key={inputIndex}>
              <Label
                forId={input.id}
                text={input.label}
                text2={input.text2}
                isRequired={input.isRequired}
              />
              {["number", "text", "date", "time"].includes(
                input.type.toLowerCase(),
              ) && (
                <CustomInput
                  placeholder={input.placeholder}
                  id={input.id}
                  name={input.name}
                  type={input.type}
                  value={newEntry?.[input.name] || ""}
                  onChange={(event) =>
                    handleChangeNewEntry(input.name, event.target.value)
                  }
                  isDisable={input?.isDisable}
                  className='form-control'
                />
              )}
              {input.type.toLowerCase() === "select" && (
                <GeneralSelect
                  defaultValue={newEntry?.[input.name] || ""}
                  placeholder='Search'
                  isClearable={false}
                  isSearchable={true}
                  isDisabled={false}
                  isLoading={false}
                  isRtl={false}
                  options={input.options}
                  handleChange={(val) =>
                    handleChangeNewEntry(input.name, val.value)
                  }
                  id={input.id}
                  name={input.name}
                />
              )}
              {input.name === "discountAmount" && Number(basePrice) > 0 && (
                <div className='text-muted mt-2' style={{ fontSize: "13px" }}>
                  Final price/person:{" "}
                  <span className='fw-bold text-dark'>
                    USD {finalPricePreview}
                  </span>
                  <span className='ms-1'>
                    (base {Number(basePrice)} − discount{" "}
                    {Number(newEntry?.discountAmount) || 0})
                  </span>
                </div>
              )}
              <ErrorFormik
                isError={
                  errors?.[fieldName] && errors?.[fieldName]?.[0]?.[input.name]
                }
                isTouched={
                  touched?.[fieldName] && touched?.[fieldName]?.[0]?.[input.name]
                }
                error={
                  errors?.[fieldName] && errors?.[fieldName]?.[0]?.[input.name]
                }
              />
            </div>
          ))}
        </div>
        {/* Action buttons BELOW the fields */}
        <div className='mt-3 d-flex justify-content-end gap-2'>
          {isEditMode ? (
            <>
              <button
                type='button'
                className='btn btn-danger light'
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                type='button'
                className='btn btn-primary d-flex align-items-center'
                onClick={handleEditFields}
              >
                <AcceptSvg width={20} height={20} />
                <span className='ms-2'>Save</span>
              </button>
            </>
          ) : (
            <button
              type='button'
              className='btn btn-primary d-flex align-items-center'
              onClick={handleAddFields}
            >
              <PlusIcon width={22} height={22} />
              <span className='ms-2'>{textButton || "Add Slot"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Table to display entered values */}
      <table className='table overflow-auto'>
        <thead>
          <tr>
            {tableLabel?.map((input) => (
              <th key={input?.label}>{input?.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(!val || val.length === 0) && (
            <tr>
              <td
                colSpan={tableLabel?.length || 1}
                className='text-center text-muted py-3'
              >
                No pricing slots yet — add one above.
              </td>
            </tr>
          )}
          {val?.map((field, index) => (
            <tr
              key={index}
              className={editIndex === index ? "table-active" : ""}
            >
              {tableLabel?.slice(0, -1)?.map((input) => (
                <td key={`${index}-${input?.name}`}>
                  {input?.name === "price"
                    ? Math.max(
                        0,
                        Number(basePrice) - (Number(field?.discountAmount) || 0),
                      )
                    : field[input?.name]}
                </td>
              ))}
              <td>
                <div className='d-flex gap-2'>
                  <button
                    type='button'
                    title='Edit'
                    disabled={isEditMode}
                    className='btn btn-sm btn-secondary sharp d-flex align-items-center justify-content-center'
                    onClick={() => handelEditField(index)}
                  >
                    <EditSvg width={18} height={18} />
                  </button>
                  <button
                    type='button'
                    title='Delete'
                    className='btn btn-sm btn-danger sharp d-flex align-items-center justify-content-center'
                    onClick={() => handleRemoveFields(index)}
                  >
                    <DeletionCircle width={18} height={18} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
